import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  ClipboardDocumentIcon,
  PaperAirplaneIcon,
  VideoCameraIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { AXIOS_INSTANCE } from '../../services/api-client';
import { useToasts } from '../../stores/useToasts';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Page, PageBody, PageHeader } from '../../components/ui/page-header';
import { Panel, PanelBody, PanelDescription, PanelHeader, PanelTitle } from '../../components/ui/panel';
import { Textarea } from '../../components/ui/textarea';

type CandidateVideoRequest = {
  id: number;
  candidate_id: number;
  candidate_display_name: string;
  candidate_email?: string | null;
  status: string;
  record_url: string;
  watch_url: string;
  expires_at: string;
  video_status?: string | null;
  email_status?: string | null;
  requires_approval?: boolean;
  approval_status?: string | null;
  engagement: {
    page_views: number;
    video_starts: number;
    video_completes: number;
  };
};

type PendingReviewSubmission = {
  id: number;
  request_id: number;
  approval_status: string;
  processing_status: string;
  processed_at?: string | null;
  duration_seconds?: number | null;
};

type CandidateForm = {
  first_name: string;
  last_name: string;
  email: string;
  linkedin_url: string;
  name_pronunciation_phonetic_override: string;
  highlights: string;
  keywords: string;
  resume: File | null;
  expires_in_days: number;
  single_use: boolean;
  requires_approval: boolean;
};

const initialForm: CandidateForm = {
  first_name: '',
  last_name: '',
  email: '',
  linkedin_url: '',
  name_pronunciation_phonetic_override: '',
  highlights: '',
  keywords: '',
  resume: null,
  expires_in_days: 7,
  single_use: true,
  requires_approval: true,
};

function statusVariant(status?: string | null): 'success' | 'warning' | 'danger' | 'secondary' | 'info' {
  if (status === 'ready' || status === 'sent' || status === 'active') return 'success';
  if (status === 'processing' || status === 'queued' || status === 'pending') return 'warning';
  if (status === 'failed' || status === 'revoked' || status === 'expired') return 'danger';
  return 'secondary';
}

function parseHighlights(text: string) {
  const lines = text.split('\n').map(line => line.trim()).filter(Boolean);
  return {
    recruiter_notes: lines,
  };
}

function canQueueCandidateEmail(request: CandidateVideoRequest) {
  if (request.video_status !== 'ready') {
    return false;
  }
  if (request.approval_status === 'pending_review' || request.approval_status === 'rejected') {
    return false;
  }
  if (request.requires_approval && request.approval_status !== 'approved') {
    return false;
  }
  return true;
}

export default function CandidateThankYouVideosPage() {
  const { push: addToast } = useToasts();
  const [form, setForm] = useState<CandidateForm>(initialForm);
  const [requests, setRequests] = useState<CandidateVideoRequest[]>([]);
  const [selected, setSelected] = useState<CandidateVideoRequest | null>(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const keywords = useMemo(
    () => form.keywords.split(',').map(value => value.trim()).filter(Boolean),
    [form.keywords],
  );

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const response = await AXIOS_INSTANCE.get('/api/v1/candidate-videos/requests', {
        params: { limit: 100 },
      });
      const items = response.data.requests || [];
      setRequests(items);
      setSelected(previous => (
        previous ? items.find((item: CandidateVideoRequest) => item.id === previous.id) || null : previous
      ));
    } catch (error: any) {
      addToast({ kind: 'error', message: error?.response?.data?.detail || 'Failed to load video requests.' });
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const createRequest = async (event: React.FormEvent) => {
    event.preventDefault();
    setCreating(true);
    try {
      const candidate = await AXIOS_INSTANCE.post('/api/v1/candidates', {
        first_name: form.first_name || null,
        last_name: form.last_name || null,
        email: form.email || null,
        linkedin_url: form.linkedin_url || null,
        name_pronunciation_phonetic_override: form.name_pronunciation_phonetic_override || null,
        highlights: parseHighlights(form.highlights),
        keywords,
      });
      if (form.resume) {
        const resumeData = new FormData();
        resumeData.append('file', form.resume);
        await AXIOS_INSTANCE.post(`/api/v1/candidates/${candidate.data.id}/resume`, resumeData);
      }
      const request = await AXIOS_INSTANCE.post('/api/v1/candidate-videos/requests', {
        candidate_id: candidate.data.id,
        expires_in_days: form.expires_in_days,
        single_use: form.single_use,
        requires_approval: form.requires_approval,
      });
      setForm(initialForm);
      addToast({ kind: 'success', message: 'Recording link created.' });
      await loadRequests();
      setSelected(request.data);
    } catch (error: any) {
      addToast({ kind: 'error', message: error?.response?.data?.detail || 'Failed to create recording link.' });
    } finally {
      setCreating(false);
    }
  };

  const copyLink = async (url: string) => {
    await navigator.clipboard.writeText(url);
    addToast({ kind: 'success', message: 'Link copied.' });
  };

  const revoke = async (requestId: number) => {
    try {
      await AXIOS_INSTANCE.post(`/api/v1/candidate-videos/requests/${requestId}/revoke`);
      addToast({ kind: 'success', message: 'Link revoked.' });
      await loadRequests();
    } catch (error: any) {
      addToast({ kind: 'error', message: error?.response?.data?.detail || 'Failed to revoke link.' });
    }
  };

  const resend = async (requestId: number) => {
    try {
      await AXIOS_INSTANCE.post(`/api/v1/candidate-videos/requests/${requestId}/resend-email`, {});
      addToast({ kind: 'success', message: 'Email queued.' });
      await loadRequests();
    } catch (error: any) {
      addToast({ kind: 'error', message: error?.response?.data?.detail || 'Failed to queue email.' });
    }
  };

  const regenerateScript = async (requestId: number) => {
    try {
      await AXIOS_INSTANCE.post(`/api/v1/candidate-videos/requests/${requestId}/regenerate-script`);
      addToast({ kind: 'success', message: 'Script regenerated.' });
    } catch (error: any) {
      addToast({ kind: 'error', message: error?.response?.data?.detail || 'Failed to regenerate script.' });
    }
  };

  const fetchLatestSubmissionId = async (requestId: number): Promise<number | null> => {
    try {
      const detail = await AXIOS_INSTANCE.get(`/api/v1/candidate-videos/requests/${requestId}`);
      const submission = detail.data?.latest_submission;
      return submission?.id ?? null;
    } catch (error) {
      return null;
    }
  };

  const approveSubmission = async (requestId: number) => {
    const submissionId = await fetchLatestSubmissionId(requestId);
    if (!submissionId) {
      addToast({ kind: 'error', message: 'No submission to approve yet.' });
      return;
    }
    try {
      await AXIOS_INSTANCE.post(
        `/api/v1/candidate-videos/submissions/${submissionId}/approve`,
        {},
      );
      addToast({ kind: 'success', message: 'Approved — email queued.' });
      await loadRequests();
    } catch (error: any) {
      addToast({ kind: 'error', message: error?.response?.data?.detail || 'Approval failed.' });
    }
  };

  const rejectSubmission = async (requestId: number) => {
    const reason = window.prompt(
      'Why are you rejecting this thank-you video? (visible in the audit log)',
    );
    if (!reason) {
      return;
    }
    const submissionId = await fetchLatestSubmissionId(requestId);
    if (!submissionId) {
      addToast({ kind: 'error', message: 'No submission to reject yet.' });
      return;
    }
    try {
      await AXIOS_INSTANCE.post(
        `/api/v1/candidate-videos/submissions/${submissionId}/reject`,
        { reason },
      );
      addToast({ kind: 'success', message: 'Rejected.' });
      await loadRequests();
    } catch (error: any) {
      addToast({ kind: 'error', message: error?.response?.data?.detail || 'Rejection failed.' });
    }
  };

  return (
    <Page layout="full-width" maxWidth="full">
      <PageHeader
        title="Thank-You Videos"
        description="Create candidate recording links and track video delivery status."
        actions={
          <Button variant="outline" onClick={loadRequests} disabled={loading}>
            <ArrowPathIcon className={loading ? 'animate-spin' : ''} />
            Refresh
          </Button>
        }
      />
      <PageBody>
        <div className="grid gap-6 lg:grid-cols-[420px_1fr]">
          <Panel>
            <PanelHeader>
              <PanelTitle>New recording link</PanelTitle>
              <PanelDescription>Candidate details and context used for the recorder script.</PanelDescription>
            </PanelHeader>
            <PanelBody>
              <form onSubmit={createRequest} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="first_name">First name</Label>
                    <Input id="first_name" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Last name</Label>
                    <Input id="last_name" value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} />
                  </div>
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required />
                </div>
                <div>
                  <Label htmlFor="resume">Resume</Label>
                  <Input id="resume" type="file" accept=".pdf,.doc,.docx,.txt" onChange={e => setForm({ ...form, resume: e.target.files?.[0] || null })} />
                </div>
                <div>
                  <Label htmlFor="linkedin_url">LinkedIn</Label>
                  <Input id="linkedin_url" value={form.linkedin_url} onChange={e => setForm({ ...form, linkedin_url: e.target.value })} placeholder="https://www.linkedin.com/in/name" />
                </div>
                <div>
                  <Label htmlFor="pronunciation">Name pronunciation</Label>
                  <Input id="pronunciation" value={form.name_pronunciation_phonetic_override} onChange={e => setForm({ ...form, name_pronunciation_phonetic_override: e.target.value })} placeholder="MEE-ruh SHAH" />
                </div>
                <div>
                  <Label htmlFor="highlights">Highlights</Label>
                  <Textarea id="highlights" value={form.highlights} onChange={e => setForm({ ...form, highlights: e.target.value })} rows={5} placeholder="One note per line" />
                </div>
                <div>
                  <Label htmlFor="keywords">Slack keywords</Label>
                  <Input id="keywords" value={form.keywords} onChange={e => setForm({ ...form, keywords: e.target.value })} placeholder="candidate name, role, company" />
                </div>
                <div className="grid grid-cols-[1fr_auto] items-end gap-3">
                  <div>
                    <Label htmlFor="expires_in_days">Expires in days</Label>
                    <Input
                      id="expires_in_days"
                      type="number"
                      min={1}
                      max={30}
                      value={form.expires_in_days}
                      onChange={e => setForm({ ...form, expires_in_days: Math.max(1, Math.min(30, Number(e.target.value) || 7)) })}
                    />
                  </div>
                  <label className="mb-2 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                    <input
                      type="checkbox"
                      checked={form.single_use}
                      onChange={e => setForm({ ...form, single_use: e.target.checked })}
                    />
                    Single use
                  </label>
                </div>
                <label className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={form.requires_approval}
                    onChange={e => setForm({ ...form, requires_approval: e.target.checked })}
                  />
                  <span>
                    Require approval before sending
                    <span className="block text-xs text-gray-500 dark:text-gray-400">
                      An approver will review the recording before the candidate email is sent.
                    </span>
                  </span>
                </label>
                <Button type="submit" variant="brand" className="w-full" disabled={creating || !form.email || (!form.first_name && !form.last_name)}>
                  {creating ? <ArrowPathIcon className="animate-spin" /> : <VideoCameraIcon />}
                  Create link
                </Button>
              </form>
            </PanelBody>
          </Panel>

          <Panel>
            <PanelHeader>
              <PanelTitle>Requests</PanelTitle>
              <PanelDescription>Recording links, processing status, and candidate engagement.</PanelDescription>
            </PanelHeader>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500 dark:border-dark-border dark:bg-dark-surface-2">
                  <tr>
                    <th className="px-6 py-3 font-medium">Candidate</th>
                    <th className="px-6 py-3 font-medium">Link</th>
                    <th className="px-6 py-3 font-medium">Video</th>
                    <th className="px-6 py-3 font-medium">Email</th>
                    <th className="px-6 py-3 font-medium">Engagement</th>
                    <th className="px-6 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-dark-border">
                  {requests.map(request => {
                    const canQueueEmail = canQueueCandidateEmail(request);
                    return (
                      <tr
                        key={request.id}
                        onClick={() => setSelected(request)}
                        className="cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-surface-2"
                      >
                        <td className="px-6 py-4">
                          <div className="font-medium text-charcoal dark:text-gray-100">{request.candidate_display_name}</div>
                          <div className="text-xs text-gray-500">{request.candidate_email}</div>
                        </td>
                        <td className="px-6 py-4">
                          <Badge variant={statusVariant(request.status)}>{request.status}</Badge>
                        </td>
                        <td className="px-6 py-4">
                          <Badge variant={statusVariant(request.video_status)}>{request.video_status || 'not submitted'}</Badge>
                          {request.approval_status === 'pending_review' && (
                            <Badge variant="warning" className="ml-2">pending review</Badge>
                          )}
                          {request.approval_status === 'rejected' && (
                            <Badge variant="danger" className="ml-2">rejected</Badge>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <Badge variant={statusVariant(request.email_status)}>{request.email_status || 'not queued'}</Badge>
                        </td>
                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300">
                          {request.engagement.page_views} views, {request.engagement.video_completes} completes
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Button size="icon-sm" variant="ghost" title="Copy recording link" onClick={(event) => { event.stopPropagation(); copyLink(request.record_url); }}>
                              <ClipboardDocumentIcon />
                            </Button>
                            <Button
                              size="icon-sm"
                              variant="ghost"
                              title={canQueueEmail ? 'Queue email' : 'Video must be ready and approved before email can be queued'}
                              disabled={!canQueueEmail}
                              onClick={(event) => {
                                event.stopPropagation();
                                if (canQueueEmail) {
                                  resend(request.id);
                                }
                              }}
                            >
                              <PaperAirplaneIcon />
                            </Button>
                            <Button size="icon-sm" variant="ghost" title="Revoke link" onClick={(event) => { event.stopPropagation(); revoke(request.id); }}>
                              <XMarkIcon />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {requests.length === 0 && (
                    <tr>
                      <td className="px-6 py-10 text-center text-gray-500" colSpan={6}>
                        No recording requests yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Panel>
        </div>

        {selected && (
          <Panel className="mt-6">
            <PanelHeader>
              <div className="flex items-center justify-between gap-4">
                <div>
                  <PanelTitle>{selected.candidate_display_name}</PanelTitle>
                  <PanelDescription>Recording and playback links for this candidate.</PanelDescription>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {selected.approval_status === 'pending_review' && (
                    <>
                      <Button variant="brand" onClick={() => approveSubmission(selected.id)}>
                        Approve & send
                      </Button>
                      <Button variant="outline" onClick={() => rejectSubmission(selected.id)}>
                        Reject
                      </Button>
                    </>
                  )}
                  <Button variant="outline" onClick={() => regenerateScript(selected.id)}>
                    <ArrowPathIcon />
                    Script
                  </Button>
                  <Button variant="outline" onClick={() => copyLink(selected.watch_url)}>
                    <ClipboardDocumentIcon />
                    Watch
                  </Button>
                </div>
              </div>
            </PanelHeader>
            {selected.approval_status === 'pending_review' && (
              <div className="mx-6 mt-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                Awaiting reviewer approval. The candidate email is held until you approve.
              </div>
            )}
            {selected.approval_status === 'rejected' && (
              <div className="mx-6 mt-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900">
                This submission was rejected. The candidate was not emailed.
              </div>
            )}
            <PanelBody className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Recording link</Label>
                <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-300">
                  {selected.record_url}
                </div>
              </div>
              <div>
                <Label>Watch link</Label>
                <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-300">
                  {selected.watch_url}
                </div>
              </div>
            </PanelBody>
          </Panel>
        )}
      </PageBody>
    </Page>
  );
}
